/**
 * AdminFeedbackAnalytics — admin-only insights into the Beta Feedback
 * engagement system. Surfaces per-surface impression+click counts with
 * CTR, a rating distribution histogram, total submissions, and a daily
 * timeline of submit_success vs impression events.
 *
 * Source: GET /api/admin/feedback-analytics?since_days=N
 * Mounted at: /admin/feedback-analytics (route added in App.js)
 * Linked from: AdminDashboard Deeper Views card list.
 */
/* eslint-disable */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  ArrowLeft, BarChart3, RefreshCw, MessageSquare, Star, TrendingUp,
  Heart, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SURFACE_LABELS = {
  dashboard_card: 'Dashboard card',
  floating_button: 'Floating button',
  beta_banner: 'Beta banner',
  smart_prompt: 'Smart prompt',
  support_page: 'Support page',
  rating_prompt: 'Rating prompt',
};

const fmtNum = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-emerald-600" />
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const AdminFeedbackAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/admin/feedback-analytics?since_days=${windowDays}`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as an admin.');
      else if (status === 403) setError('Admin access required.');
      else setError('Could not load analytics.');
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // Headline metrics derived from the response.
  const headline = useMemo(() => {
    if (!data) return { impressions: 0, clicks: 0, ctr: null, ratings: 0, avgRating: null };
    const surfaces = data.by_surface || {};
    let imp = 0, clk = 0, ratings = 0, ratingTotal = 0;
    Object.values(surfaces).forEach((c) => {
      imp += c.impression || 0;
      clk += c.click || 0;
    });
    Object.entries(data.rating_distribution || {}).forEach(([k, n]) => {
      ratings += n;
      ratingTotal += parseInt(k, 10) * n;
    });
    return {
      impressions: imp,
      clicks: clk,
      ctr: imp ? Math.round((clk / imp) * 1000) / 10 : null,
      ratings,
      avgRating: ratings ? Math.round((ratingTotal / ratings) * 10) / 10 : null,
    };
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <p className="text-sm text-gray-900 mb-3" data-testid="feedback-analytics-error">{error}</p>
        <Button variant="outline" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to admin
        </Button>
      </div>
    );
  }

  const surfaces = data?.by_surface || {};
  const surfaceKeys = Object.keys(surfaces);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-feedback-analytics-page">
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} aria-label="Back to admin">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-amber-600" />
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Beta Feedback Analytics</h1>
              </div>
              <p className="text-[11px] text-gray-500">Engagement across all feedback surfaces</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(parseInt(v, 10))}>
              <SelectTrigger className="w-[120px]" data-testid="analytics-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 space-y-5">
        {/* Headline stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-headline">
          <StatCard icon={MessageSquare} label="Impressions" value={fmtNum(headline.impressions)} sub="Surfaces shown" testid="stat-impressions" />
          <StatCard icon={TrendingUp} label="Clicks" value={fmtNum(headline.clicks)} sub="Across all CTAs" testid="stat-clicks" />
          <StatCard icon={BarChart3} label="CTR" value={headline.ctr == null ? '—' : `${headline.ctr}%`} sub="Click-through rate" testid="stat-ctr" />
          <StatCard icon={Heart} label="Submissions" value={fmtNum(data?.total_submissions || 0)} sub={headline.avgRating ? `Avg rating ${headline.avgRating} ★` : ''} testid="stat-submissions" />
        </div>

        {/* Per-surface table */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="per-surface-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Per surface</CardTitle>
            <CardDescription className="text-xs">Impression → click conversion for each feedback surface</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {surfaceKeys.length === 0 ? (
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                No events yet in this window — once beta users start engaging, totals will appear here.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-left text-xs">
                    <th className="font-medium py-2 pr-3">Surface</th>
                    <th className="font-medium py-2 pr-3 text-right">Impressions</th>
                    <th className="font-medium py-2 pr-3 text-right">Clicks</th>
                    <th className="font-medium py-2 pr-3 text-right">Submits</th>
                    <th className="font-medium py-2 pr-3 text-right">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {surfaceKeys.map((s) => {
                    const c = surfaces[s];
                    return (
                      <tr key={s} className="border-t border-gray-50" data-testid={`surface-row-${s}`}>
                        <td className="py-2.5 pr-3 text-gray-800">{SURFACE_LABELS[s] || s}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(c.impression || 0)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(c.click || 0)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(c.submit_success || 0)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700">
                          {c.ctr == null ? '—' : `${c.ctr}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Rating distribution histogram */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="rating-distribution-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Star className="h-4 w-4 text-amber-500" /> Rating distribution
            </CardTitle>
            <CardDescription className="text-xs">
              Stars tapped on the Smart Feedback Prompt — independent of whether the user completed the full form.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {(() => {
              const dist = data?.rating_distribution || {};
              const maxN = Math.max(1, ...Object.values(dist).map(Number));
              const total = Object.values(dist).reduce((a, b) => a + b, 0);
              if (!total) return <p className="text-xs text-gray-500">No ratings yet.</p>;
              return (
                <div className="space-y-1.5">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const n = dist[String(star)] || 0;
                    const pct = total ? Math.round((n / total) * 100) : 0;
                    return (
                      <div key={star} className="flex items-center gap-2 text-xs" data-testid={`rating-bar-${star}`}>
                        <span className="w-6 text-amber-500 flex items-center gap-0.5">
                          {star}<Star className="h-3 w-3 fill-amber-400" />
                        </span>
                        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-3 bg-gradient-to-r from-emerald-400 to-emerald-600"
                            style={{ width: `${(n / maxN) * 100}%` }}
                          />
                        </div>
                        <span className="w-16 text-right tabular-nums text-gray-600">
                          {n} <span className="text-gray-400">({pct}%)</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminFeedbackAnalytics;
